import { useEffect, useRef, useState } from 'react'
import * as THREE from 'three'
import { createFactoryConveyor } from './FactoryConveyorScene.mjs'
import { createFactoryFacility } from './FactoryFacilityScene.mjs'
import { createFactoryQuality } from './FactoryQualityScene.mjs'
import { advanceFactoryView } from './factory-view-motion.mjs'

const palette = { line: '#1674cf', white: '#ffffff', pale: '#c7e6f5', side: '#69bcef', blue: '#0082e5', ground: '#e3eaf0' }
const viewDirection = new THREE.Vector3(1, Math.sqrt(2 / 3), 1).normalize()

function createScene(kind) {
  if (kind === 'efficiency') return createFactoryConveyor({ palette, viewDirection })
  if (kind === 'owned') return createFactoryFacility({ palette })
  return createFactoryQuality({ palette })
}

export default function FactoryScene({ kind, selected = false, orientation, label, fallback }) {
  const hostRef = useRef(null)
  const canvasRef = useRef(null)
  const selectedRef = useRef(selected)
  selectedRef.current = selected
  const orientationRef = useRef(orientation)
  orientationRef.current = orientation
  const [ready, setReady] = useState(false)

  useEffect(() => {
    setReady(false)
    const host = hostRef.current, canvas = canvasRef.current
    let renderer
    try {
      renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true, preserveDrawingBuffer: true })
    } catch {
      return
    }
    renderer.setClearColor(0xffffff, 0)
    renderer.outputColorSpace = THREE.SRGBColorSpace
    const scene = new THREE.Scene()
    const model = createScene(kind)
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)')
    const camera = new THREE.OrthographicCamera(-4, 4, 3, -3, .1, 40)
    const bounds = model.framingBounds ?? new THREE.Box3().setFromObject(model.root)
    const center = bounds.getCenter(new THREE.Vector3())
    const pivot = new THREE.Group()
    pivot.position.copy(center)
    model.root.position.sub(center)
    pivot.add(model.root)
    scene.add(pivot)
    camera.position.copy(center).addScaledVector(viewDirection, 12)
    camera.lookAt(center)
    camera.updateMatrixWorld()
    // Reserve the full yaw envelope; the conveyor deliberately keeps its short framing span.
    const framingPoints = kind === 'efficiency' ? model.framingPoints : []
    if (kind !== 'efficiency') {
      for (const x of [bounds.min.x, bounds.max.x]) for (const y of [bounds.min.y, bounds.max.y]) for (const z of [bounds.min.z, bounds.max.z]) framingPoints.push(new THREE.Vector3(x, y, z))
    }
    const projected = [], vertical = new THREE.Vector3(0, 1, 0)
    const minimumYaw = kind === 'efficiency' ? 0 : -45
    const maximumYaw = kind === 'efficiency' ? 0 : 45
    for (let degrees = minimumYaw; degrees <= maximumYaw; degrees++) {
      for (const point of framingPoints) projected.push(point.clone().sub(center).applyAxisAngle(vertical, THREE.MathUtils.degToRad(degrees)).add(center).applyMatrix4(camera.matrixWorldInverse))
    }
    const minX = Math.min(...projected.map(point => point.x)), maxX = Math.max(...projected.map(point => point.x))
    const minY = Math.min(...projected.map(point => point.y)), maxY = Math.max(...projected.map(point => point.y))
    const width = maxX - minX, height = maxY - minY
    const centerX = (minX + maxX) / 2, centerY = (minY + maxY) / 2
    let lost = false, visible = false, disposed = false, hasPlayed = false
    let frame = 0, elapsed = 0, previous = null
    const baseElevation = Math.asin(viewDirection.y)
    let pointerYaw = 0, yaw = 0
    let pitch = 0
    let approach = { value: 0, velocity: 0 }
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)')
    const mobileViewport = window.matchMedia('(max-width: 600px)')
    const card = host.closest('.factory-advantage')
    let touchGesture = null
    let suppressClick = false
    function yawAt(clientX) {
      return THREE.MathUtils.clamp(clientX / window.innerWidth * 2 - 1, -1, 1) * Math.PI / 4
    }
    function onPointerDown(event) {
      if (event.pointerType !== 'touch' || !mobileViewport.matches) return
      if (!event.isPrimary) { resetPointer(); return }
      if (!card?.contains(event.target)) return
      suppressClick = false
      touchGesture = { id: event.pointerId, x: event.clientX, y: event.clientY, horizontal: false }
    }
    function onPointerMove(event) {
      if (event.pointerType === 'touch' && touchGesture?.id === event.pointerId) {
        const dx = Math.abs(event.clientX - touchGesture.x), dy = Math.abs(event.clientY - touchGesture.y)
        if (!touchGesture.horizontal) {
          if (dy > 8 && dy >= dx) { touchGesture = null; return }
          if (dx <= 8 || dx <= dy) return
          touchGesture.horizontal = true
          suppressClick = true
        }
        pointerYaw = yawAt(event.clientX)
        return
      }
      if (event.pointerType !== 'mouse' || !finePointer.matches) return
      pointerYaw = yawAt(event.clientX)
    }
    function onPointerEnd(event) {
      if (touchGesture?.id === event.pointerId) resetPointer()
    }
    function resetPointer() { pointerYaw = 0; touchGesture = null }
    function onClick(event) {
      if (!suppressClick) return
      suppressClick = false
      if (event.detail === 0 && event.pointerType !== 'touch') return
      event.preventDefault()
      event.stopPropagation()
    }
    card?.addEventListener('click', onClick, true)
    mobileViewport.addEventListener('change', resetPointer)
    window.addEventListener('pointerdown', onPointerDown, { passive: true })
    window.addEventListener('pointermove', onPointerMove, { passive: true })
    window.addEventListener('pointerup', onPointerEnd, { passive: true })
    window.addEventListener('pointercancel', onPointerEnd, { passive: true })
    window.addEventListener('blur', resetPointer)
    document.addEventListener('pointerleave', resetPointer)
    function draw() {
      if (lost || !host.clientWidth || !host.clientHeight) return
      if (renderer.getContext().isContextLost()) {
        setReady(false)
        return
      }
      pivot.rotation.y = yaw
      const proximity = approach.value
      const azimuth = Math.PI / 4 * (1 - proximity)
      const elevation = THREE.MathUtils.lerp(baseElevation, .06, proximity) + pitch
      camera.position.set(
        center.x + 12 * Math.sin(azimuth) * Math.cos(elevation),
        center.y + 12 * Math.sin(elevation),
        center.z + 12 * Math.cos(azimuth) * Math.cos(elevation),
      )
      camera.lookAt(center)
      camera.zoom = 1 + proximity * (kind === 'quality' ? .4 : .25)
      const offsetX = (camera.left + camera.right) / 2
      const offsetY = (camera.top + camera.bottom) / 2
      const desiredX = centerX * (1 - proximity)
      const desiredY = centerY * (1 - proximity)
      camera.left += desiredX - offsetX
      camera.right += desiredX - offsetX
      camera.top += desiredY - offsetY
      camera.bottom += desiredY - offsetY
      camera.updateProjectionMatrix()
      host.dataset.proximity = proximity.toFixed(4)
      host.dataset.cameraZoom = camera.zoom.toFixed(4)
      host.dataset.cameraElevation = THREE.MathUtils.radToDeg(elevation).toFixed(4)
      host.dataset.yaw = THREE.MathUtils.radToDeg(yaw).toFixed(4)
      if (model.update) {
        const state = model.update(reduced.matches || !hasPlayed ? model.staticTime ?? 0 : elapsed)
        host.dataset.animationTime = elapsed.toFixed(4)
        host.dataset.cycleSeconds = String(model.cycleSeconds)
        if (kind === 'quality') {
          host.dataset.testStage = state.stage
          host.dataset.press = state.press.toFixed(4)
          host.dataset.screen = state.success ? 'success' : 'loading'
          host.dataset.handleAngle = state.handleAngle.toFixed(4)
          host.dataset.handlePivot = JSON.stringify(state.handlePivot)
          host.dataset.handleGrip = JSON.stringify(state.gripPosition)
        }
        if (kind === 'owned') {
          host.dataset.truckState = state.truck.state
          host.dataset.truckX = state.truck.x.toFixed(4)
          host.dataset.cargo = JSON.stringify(state.cargo)
        }
        if (kind === 'efficiency') {
          host.dataset.cargo = JSON.stringify(state.packages)
          host.dataset.transportSpeed = String(state.cargoSpeed)
          host.dataset.routeStart = String(state.routeStart)
          host.dataset.routeEnd = String(state.routeEnd)
        }
      }
      model.faceCamera?.(camera)
      renderer.render(scene, camera)
      setReady(!renderer.getContext().isContextLost())
    }
    function resize() {
      if (!host.clientWidth || !host.clientHeight) return
      // Supersample fine outlines, with headroom for the selected card's scale.
      const pixelRatio = Math.min(Math.max((window.devicePixelRatio || 1) * 1.5, 2), 4)
      if (renderer.getPixelRatio() !== pixelRatio) renderer.setPixelRatio(pixelRatio)
      const aspect = host.clientWidth / host.clientHeight
      const halfHeight = Math.max(height / 2, width / (2 * aspect)) * 1.02
      camera.left = centerX - halfHeight * aspect
      camera.right = centerX + halfHeight * aspect
      camera.top = centerY + halfHeight
      camera.bottom = centerY - halfHeight
      camera.updateProjectionMatrix()
      renderer.setSize(host.clientWidth, host.clientHeight, false)
      draw()
    }
    let resolutionQuery
    function watchResolution() {
      resolutionQuery?.removeEventListener('change', onResolutionChange)
      resolutionQuery = window.matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`)
      resolutionQuery.addEventListener('change', onResolutionChange)
    }
    function onResolutionChange() { watchResolution(); resize() }
    function pause() {
      cancelAnimationFrame(frame)
      frame = 0
      previous = null
    }
    function tick(now) {
      frame = 0
      if (disposed || lost) return
      // Media values can update before their change event reaches this scene.
      if (reduced.matches) { syncAnimation(); return }
      if (!visible || document.hidden) return
      const delta = previous === null ? 0 : Math.min((now - previous) / 1000, .1)
      elapsed += delta
      const tracksTouch = mobileViewport.matches && touchGesture?.horizontal
      const inspecting = selectedRef.current && finePointer.matches && !mobileViewport.matches && document.hasFocus()
      approach = advanceFactoryView(approach, inspecting ? 1 : 0, delta)
      const motion = orientationRef.current
      const tracksOrientation = motion?.active && !tracksTouch
      const target = tracksOrientation ? motion.yaw : tracksTouch || selectedRef.current && finePointer.matches ? pointerYaw : 0
      yaw = THREE.MathUtils.damp(yaw, target, 14, delta)
      pitch = THREE.MathUtils.damp(pitch, tracksOrientation ? motion.pitch : 0, 10, delta)
      if (Math.abs(yaw - target) < .00001) yaw = target
      previous = now
      draw()
      frame = requestAnimationFrame(tick)
    }
    function syncAnimation() {
      pause()
      if (document.hidden || reduced.matches) resetPointer()
      if (!model.update || disposed) return
      const running = visible && !lost && !document.hidden && !reduced.matches
      host.dataset.animationState = reduced.matches ? 'reduced' : running ? 'running' : 'paused'
      if (reduced.matches) { yaw = 0; pitch = 0; approach = { value: 0, velocity: 0 }; draw() }
      if (running) {
        hasPlayed = true
        draw()
        frame = requestAnimationFrame(tick)
      }
    }
    function onLost(event) {
      event.preventDefault()
      lost = true
      setReady(false)
      syncAnimation()
    }
    function onRestored() { lost = false; resize(); syncAnimation() }
    canvas.addEventListener('webglcontextlost', onLost)
    canvas.addEventListener('webglcontextrestored', onRestored)
    const observer = new ResizeObserver(resize)
    observer.observe(host)
    const visibility = new IntersectionObserver(entries => {
      visible = entries[0].isIntersecting
      syncAnimation()
    })
    if (model.update) {
      visibility.observe(host)
      document.addEventListener('visibilitychange', syncAnimation)
      reduced.addEventListener('change', syncAnimation)
      syncAnimation()
    }
    watchResolution()
    resize()
    return () => {
      disposed = true
      pause()
      observer.disconnect()
      visibility.disconnect()
      document.removeEventListener('visibilitychange', syncAnimation)
      reduced.removeEventListener('change', syncAnimation)
      resolutionQuery.removeEventListener('change', onResolutionChange)
      card?.removeEventListener('click', onClick, true)
      mobileViewport.removeEventListener('change', resetPointer)
      window.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerEnd)
      window.removeEventListener('pointercancel', onPointerEnd)
      window.removeEventListener('blur', resetPointer)
      document.removeEventListener('pointerleave', resetPointer)
      canvas.removeEventListener('webglcontextlost', onLost)
      canvas.removeEventListener('webglcontextrestored', onRestored)
      model.dispose()
      renderer.dispose()
    }
  }, [kind])

  return <div ref={hostRef} className="factory-illustration" data-renderer={ready ? 'three' : 'fallback'} data-view="isometric" data-palette="blue-white-gray">
    <canvas ref={canvasRef} role="img" aria-label={label} aria-hidden={!ready} />
    {!ready && fallback}
  </div>
}
